import { useState, useEffect } from 'react';
import { 
  LogIn, Award, Target, ArrowLeft, Shield, Zap, 
  CheckCircle, Star, Trophy, Users, Loader,
  BadgeCheck, Lock, CreditCard, TrendingUp, Sparkles,
  Car, BookOpen, Clock, GraduationCap, Rocket, Crown,
  Globe, CheckCircle2, PlayCircle, ChevronRight, Heart,
  MapPin, Play, ArrowRight, Mail, Eye, EyeOff, Phone, User
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage({ onBack, onLoginSuccess }) {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [stats, setStats] = useState({ questions: 0, users: 0, success: 0 });
  const [loading, setLoading] = useState(false);
  const [hoverCard, setHoverCard] = useState(false);
  const [loginPageSettings, setLoginPageSettings] = useState(null);
  
  // Auth mode state
  const [authMode, setAuthMode] = useState('choice'); // 'choice', 'login', 'register', 'forgot'
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Default testimonials - will be replaced by database data if available
  const defaultTestimonials = [
    {
      name: "Ayşe K.",
      text: "İlk denemede geçtim! Sorular gerçek sınava çok benziyordu. Kesinlikle tavsiye ederim.",
      rating: 5,
      location: "Stockholm",
      avatar: "👩‍💼",
      passedDate: "2024"
    },
    {
      name: "Mehmet Y.",
      text: "Türkçe açıklamalar sayesinde konuları çok daha iyi anladım. 3 günde hazırlandım!",
      rating: 5,
      location: "Göteborg",
      avatar: "👨‍💻",
      passedDate: "2024"
    },
    {
      name: "Fatma S.",
      text: "Premium paket sayesinde sınırsız pratik yaptım. %92 ile geçtim!",
      rating: 5,
      location: "Malmö",
      avatar: "👩‍🎓",
      passedDate: "2024"
    },
    {
      name: "Ali R.",
      text: "Mobil uyumlu olması harika. Her yerde çalışabildim. 5 yıldız!",
      rating: 5,
      location: "Uppsala",
      avatar: "👨‍🔧",
      passedDate: "2024"
    }
  ];
  
  const [testimonials, setTestimonials] = useState(defaultTestimonials);

  // Fetch testimonials from database
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch(`${API}/testimonials`);
        const data = await response.json();
        if (data && data.length > 0) {
          const mappedTestimonials = data.map(t => ({
            name: t.name,
            text: t.text,
            rating: t.rating || 5,
            location: t.location,
            avatar: getAvatarForName(t.name),
            passedDate: t.date || "2024"
          }));
          setTestimonials(mappedTestimonials);
        }
      } catch (error) {
        console.log('Using default testimonials');
      }
    };
    
    fetchTestimonials();
  }, []);

  const getAvatarForName = (name) => {
    const avatars = ["👩‍💼", "👨‍💻", "👩‍🎓", "👨‍🔧", "👩‍🏫", "👨‍💼"];
    const index = name.charCodeAt(0) % avatars.length;
    return avatars[index];
  };

  // Testimonial rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Fetch real stats from API and animate
  useEffect(() => {
    // First fetch homepage settings for login page customization
    const fetchLoginSettings = async () => {
      try {
        const response = await fetch(`${API}/homepage/settings`);
        const data = await response.json();
        if (data && data.login_page) {
          setLoginPageSettings(data.login_page);
        }
      } catch (error) {
        console.error('Login settings fetch error:', error);
      }
    };
    fetchLoginSettings();
    
    const fetchRealStats = async () => {
      try {
        const response = await fetch(`${API}/stats`);
        const data = await response.json();
        
        // Get target values from settings or use defaults
        const targetQuestions = loginPageSettings?.stat_questions || Math.max(2000, data.total_questions || 2000);
        const targetSuccess = loginPageSettings?.stat_success_rate || 95;
        const targetUsers = loginPageSettings?.stat_users || 1000;
        
        let currentQ = 0;
        let currentU = 0;
        let currentS = 0;
        
        const questionInterval = setInterval(() => {
          currentQ += Math.ceil(targetQuestions / 40);
          if (currentQ >= targetQuestions) {
            currentQ = targetQuestions;
            clearInterval(questionInterval);
          }
          setStats(prev => ({ ...prev, questions: currentQ }));
        }, 25);
        
        const userInterval = setInterval(() => {
          currentU += Math.ceil(targetUsers / 20);
          if (currentU >= targetUsers) {
            currentU = targetUsers;
            clearInterval(userInterval);
          }
          setStats(prev => ({ ...prev, users: currentU }));
        }, 25);
        
        const successInterval = setInterval(() => {
          currentS += 3;
          if (currentS >= targetSuccess) {
            currentS = targetSuccess;
            clearInterval(successInterval);
          }
          setStats(prev => ({ ...prev, success: currentS }));
        }, 35);
        
        return () => {
          clearInterval(questionInterval);
          clearInterval(userInterval);
          clearInterval(successInterval);
        };
      } catch (error) {
        console.error('Stats fetch error:', error);
        setStats({ questions: 2000, users: 5000, success: 95 });
      }
    };
    
    fetchRealStats();
  }, []);

  const handleGoogleLogin = () => {
    setLoading(true);
    setIsRedirecting(true);
    setTimeout(() => {
      const redirectUrl = window.location.origin + '/dashboard';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }, 500);
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      }, { withCredentials: true });
      
      if (response.data) {
        // Use callback instead of direct navigation to avoid 404 flash
        if (onLoginSuccess) {
          onLoginSuccess(response.data.user);
        } else {
          window.location.href = '/dashboard';
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Giriş yapılamadı. Email veya şifre hatalı.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    if (formData.password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır');
      setLoading(false);
      return;
    }
    
    if (!formData.phone || !formData.phone.trim()) {
      setError('Telefon numarası zorunludur');
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null
      }, { withCredentials: true });
      
      if (response.data) {
        // Use callback instead of direct navigation
        if (onLoginSuccess) {
          onLoginSuccess(response.data.user);
        } else {
          window.location.href = '/dashboard';
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Kayıt oluşturulamadı. Bu email zaten kullanılıyor olabilir.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: formData.email
      });
      
      setSuccessMessage('Şifre sıfırlama linki email adresinize gönderildi. Lütfen inbox ve spam klasörünüzü kontrol edin.');
    } catch (err) {
      setError('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  // Redirecting Screen
  if (isRedirecting) {
    return (
      <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-950 via-red-950 to-red-900 flex items-center justify-center">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-[600px] h-[600px] bg-red-500/20 rounded-full blur-[120px] animate-pulse top-0 left-0"></div>
          <div className="absolute w-[600px] h-[600px] bg-red-600/20 rounded-full blur-[120px] animate-pulse bottom-0 right-0"></div>
        </div>
        
        <div className="relative z-10 text-center max-w-lg mx-auto px-6">
          <div className="bg-white/5 backdrop-blur-3xl border border-white/10 rounded-[2rem] p-16 shadow-2xl">
            <div className="relative mb-10">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-36 h-36 bg-gradient-to-br from-red-500 to-red-600 rounded-full animate-ping opacity-40"></div>
              </div>
              <div className="relative w-36 h-36 mx-auto bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-2xl shadow-red-500/40">
                <Loader className="w-20 h-20 text-white animate-spin" />
              </div>
            </div>
            
            <h2 className="text-4xl font-black text-white mb-5">
              Yönlendiriliyorsunuz...
            </h2>
            <p className="text-white/60 text-xl mb-8">
              Google ile güvenli giriş için lütfen bekleyin
            </p>
            
            <div className="flex items-center justify-center gap-3 text-white/40 text-sm">
              <Shield className="w-5 h-5" />
              <span>256-bit SSL Şifreli Bağlantı</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render auth form based on mode
  const renderAuthForm = () => {
    // Choice screen - Google or Email
    if (authMode === 'choice') {
      return (
        <>
          {/* Card Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-3xl mb-5 shadow-xl shadow-red-500/30">
              <Rocket className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-black text-white mb-2">
              Hemen Başla!
            </h2>
            <p className="text-white/50 text-base">
              Giriş yap veya ücretsiz hesap oluştur
            </p>
          </div>
          
          {/* Google Login Button */}
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full bg-white hover:bg-gray-50 text-gray-800 font-bold text-base py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed mb-4 group"
          >
            {loading ? (
              <Loader className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Google ile Devam Et</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
          
          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-slate-900/80 px-4 text-white/40 text-sm font-medium">veya</span>
            </div>
          </div>
          
          {/* Email Login/Register Buttons */}
          <button
            onClick={() => setAuthMode('login')}
            className="w-full bg-gradient-to-r from-red-600 to-red-600 hover:from-red-500 hover:to-red-500 text-white font-bold text-base py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:scale-[1.02] mb-3"
          >
            <Mail className="w-5 h-5" />
            <span>Email ile Giriş Yap</span>
          </button>
          
          <button
            onClick={() => setAuthMode('register')}
            className="w-full bg-white/10 hover:bg-white/20 text-white font-bold text-base py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-3 border border-white/20"
          >
            <User className="w-5 h-5" />
            <span>Yeni Hesap Oluştur</span>
          </button>
          
          {/* Demo Button */}
          <button
            onClick={onBack}
            className="w-full mt-4 text-white/60 hover:text-white text-sm font-medium py-3 transition-all flex items-center justify-center gap-2"
          >
            <Play className="w-4 h-4" />
            <span>Ücretsiz Demo Test</span>
          </button>
        </>
      );
    }
    
    // Login Form
    if (authMode === 'login') {
      return (
        <>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-black text-white mb-2">Giriş Yap</h2>
            <p className="text-white/50 text-sm">Email ve şifrenizle giriş yapın</p>
          </div>
          
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 mb-4 text-red-300 text-sm">
              <div className="font-semibold mb-1">⚠️ Hata:</div>
              {error}
              {error.includes('Google') && (
                <button
                  onClick={handleGoogleLogin}
                  className="mt-3 w-full bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition-all"
                >
                  <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
                  Google ile Giriş Yap
                </button>
              )}
            </div>
          )}
          
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label className="text-white/70 text-sm mb-1 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="w-full bg-white/5 border border-white/20 rounded-xl py-3 pl-11 pr-4 text-white placeholder-white/30 focus:outline-none focus:border-red-500/50"
                  placeholder="ornek@email.com"
                />
              </div>
            </div>
            
            <div>
              <label className="text-white/70 text-sm mb-1 block">Şifre</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  className="w-full bg-white/5 border border-white/20 rounded-xl py-3 pl-11 pr-11 text-white placeholder-white/30 focus:outline-none focus:border-red-500/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-red-600 to-red-600 hover:from-red-500 hover:to-red-500 text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader className="w-5 h-5 animate-spin" /> : 'Giriş Yap'}
            </button>
          </form>
          
          <button
            onClick={() => { setAuthMode('forgot'); setError(''); }}
            className="w-full text-white/50 hover:text-white/70 text-sm mt-4 transition-all"
          >
            Şifremi Unuttum
          </button>
          
          <div className="border-t border-white/10 mt-6 pt-4">
            <button
              onClick={() => { setAuthMode('choice'); setError(''); }}
              className="w-full text-white/60 hover:text-white text-sm flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Geri Dön
            </button>
          </div>
        </>
      );
    }
    
    // Register Form
    if (authMode === 'register') {
      return (
        <>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-black text-white mb-2">Hesap Oluştur</h2>
            <p className="text-white/50 text-sm">Hemen ücretsiz kayıt olun</p>
          </div>
          
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 mb-4 text-red-300 text-sm">
              <div className="font-semibold mb-1">⚠️ Hata:</div>
              {error}
              {error.includes('Google') && (
                <button
                  onClick={handleGoogleLogin}
                  className="mt-3 w-full bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition-all"
                >
                  <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
                  Google ile Giriş Yap
                </button>
              )}
            </div>
          )}
          
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="text-white/70 text-sm mb-1 block">Ad Soyad</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="w-full bg-white/5 border border-white/20 rounded-xl py-3 pl-11 pr-4 text-white placeholder-white/30 focus:outline-none focus:border-red-500/50"
                  placeholder="Adınız Soyadınız"
                />
              </div>
            </div>
            
            <div>
              <label className="text-white/70 text-sm mb-1 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="w-full bg-white/5 border border-white/20 rounded-xl py-3 pl-11 pr-4 text-white placeholder-white/30 focus:outline-none focus:border-red-500/50"
                  placeholder="ornek@email.com"
                />
              </div>
            </div>
            
            <div>
              <label className="text-white/70 text-sm mb-1 block">Telefon <span className="text-red-400">*</span></label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  data-testid="register-phone-input"
                  className="w-full bg-white/5 border border-white/20 rounded-xl py-3 pl-11 pr-4 text-white placeholder-white/30 focus:outline-none focus:border-red-500/50"
                  placeholder="+46 70 123 45 67"
                />
              </div>
            </div>
            
            <div>
              <label className="text-white/70 text-sm mb-1 block">Şifre (min 6 karakter)</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  minLength={6}
                  className="w-full bg-white/5 border border-white/20 rounded-xl py-3 pl-11 pr-11 text-white placeholder-white/30 focus:outline-none focus:border-red-500/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-red-600 to-red-600 hover:from-red-500 hover:to-red-500 text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader className="w-5 h-5 animate-spin" /> : 'Hesap Oluştur'}
            </button>
          </form>
          
          <div className="border-t border-white/10 mt-6 pt-4">
            <button
              onClick={() => { setAuthMode('choice'); setError(''); }}
              className="w-full text-white/60 hover:text-white text-sm flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Geri Dön
            </button>
          </div>
        </>
      );
    }
    
    // Forgot Password Form
    if (authMode === 'forgot') {
      return (
        <>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-black text-white mb-2">Şifremi Unuttum</h2>
            <p className="text-white/50 text-sm">Email adresinize şifre sıfırlama linki göndereceğiz</p>
          </div>
          
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 mb-4 text-red-300 text-sm">
              <div className="font-semibold mb-1">⚠️ Hata:</div>
              {error}
            </div>
          )}
          
          {successMessage && (
            <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-3 mb-4 text-green-300 text-sm">
              {successMessage}
            </div>
          )}
          
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="text-white/70 text-sm mb-1 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="w-full bg-white/5 border border-white/20 rounded-xl py-3 pl-11 pr-4 text-white placeholder-white/30 focus:outline-none focus:border-red-500/50"
                  placeholder="ornek@email.com"
                />
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-red-600 to-red-600 hover:from-red-500 hover:to-red-500 text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader className="w-5 h-5 animate-spin" /> : 'Şifre Sıfırlama Linki Gönder'}
            </button>
          </form>
          
          <div className="border-t border-white/10 mt-6 pt-4">
            <button
              onClick={() => { setAuthMode('login'); setError(''); setSuccessMessage(''); }}
              className="w-full text-white/60 hover:text-white text-sm flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Giriş Sayfasına Dön
            </button>
          </div>
        </>
      );
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-950 via-red-950 to-red-900">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-[800px] h-[800px] bg-red-600/15 rounded-full blur-[150px] animate-pulse -top-60 -left-60"></div>
        <div className="absolute w-[800px] h-[800px] bg-red-600/15 rounded-full blur-[150px] animate-pulse -top-60 -right-60 animation-delay-2000"></div>
        <div className="absolute w-[800px] h-[800px] bg-red-600/15 rounded-full blur-[150px] animate-pulse -bottom-60 left-1/2 -translate-x-1/2 animation-delay-4000"></div>
        
        {/* Floating Cars */}
        <div className="absolute top-32 left-[15%] text-8xl opacity-10 animate-float">🚗</div>
        <div className="absolute top-48 right-[20%] text-7xl opacity-10 animate-float animation-delay-1000">🇸🇪</div>
        <div className="absolute bottom-48 left-[25%] text-6xl opacity-10 animate-float animation-delay-2000">✅</div>
        <div className="absolute bottom-32 right-[15%] text-8xl opacity-10 animate-float animation-delay-3000">🏆</div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 py-6 min-h-screen flex flex-col">
        {/* Header */}
        <header className="flex justify-between items-center mb-6">
          <div 
            className="flex items-center gap-4 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={onBack}
          >
            <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center shadow-xl shadow-red-500/30">
              <Car className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-white tracking-tight">Ehliyet.nu</h1>
              <p className="text-red-300/60 text-sm font-medium">İsveç Ehliyet Sınavına Hazırlık</p>
            </div>
          </div>
          
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-all px-5 py-2.5 rounded-xl hover:bg-white/5 border border-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Ana Sayfa</span>
            </button>
          )}
        </header>

        {/* Hero Section */}
        <div className="flex-1 flex items-center justify-center py-4">
          <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Left Side - Hero Content */}
            <div className="text-center lg:text-left order-2 lg:order-1 hidden lg:block">
              {/* Badge */}
              <div className="inline-flex items-center gap-2.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/30 rounded-full px-5 py-2.5 mb-8">
                <Crown className="w-5 h-5 text-amber-400" />
                <span className="text-amber-300 font-bold">İsveç&apos;in #1 Türkçe Ehliyet Platformu</span>
              </div>
              
              {/* Main Headline */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-8 leading-[1.1]">
                <span className="block text-white mb-2">{loginPageSettings?.title_line1 || 'İsveç Ehliyet'}</span>
                <span className="block bg-gradient-to-r from-red-400 via-red-400 to-orange-400 bg-clip-text text-transparent">
                  {loginPageSettings?.title_line2 || 'Sınavına Hazırlan!'}
                </span>
              </h1>
              
              {/* Subtitle */}
              <p className="text-xl sm:text-2xl text-white/60 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                {loginPageSettings?.subtitle || `Türkçe açıklamalar, gerçek sınav formatı ve %${stats.success} başarı oranı ile ilk seferde geç!`}
              </p>
              
              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-4 mb-10 max-w-xl mx-auto lg:mx-0">
                <div className="text-center p-5 bg-gradient-to-br from-red-500/10 to-red-600/5 backdrop-blur-sm rounded-2xl border border-red-400/20 hover:border-red-400/40 transition-all">
                  <div className="text-4xl font-black text-red-400 mb-1">{stats.questions.toLocaleString()}+</div>
                  <div className="text-white/50 text-sm font-medium">Güncel Soru</div>
                </div>
                <div className="text-center p-5 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 backdrop-blur-sm rounded-2xl border border-emerald-400/20 hover:border-emerald-400/40 transition-all">
                  <div className="text-4xl font-black text-emerald-400 mb-1">%{stats.success}</div>
                  <div className="text-white/50 text-sm font-medium">Başarı Oranı</div>
                </div>
                <div className="text-center p-5 bg-gradient-to-br from-orange-500/10 to-orange-600/5 backdrop-blur-sm rounded-2xl border border-orange-400/20 hover:border-orange-400/40 transition-all">
                  <div className="text-4xl font-black text-orange-400 mb-1">{stats.users.toLocaleString()}+</div>
                  <div className="text-white/50 text-sm font-medium">Mutlu Kullanıcı</div>
                </div>
              </div>
              
              {/* Testimonial Carousel */}
              <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-6 max-w-xl mx-auto lg:mx-0">
                <div className="flex items-start gap-4">
                  <div className="text-5xl flex-shrink-0">{testimonials[currentTestimonial].avatar}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-white font-bold text-lg">{testimonials[currentTestimonial].name}</span>
                      <div className="flex items-center gap-1 text-white/40 text-sm">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{testimonials[currentTestimonial].location}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                      ))}
                    </div>
                    <p className="text-white/70 text-sm leading-relaxed">&quot;{testimonials[currentTestimonial].text}&quot;</p>
                  </div>
                </div>
                
                {/* Carousel indicators */}
                <div className="flex justify-center gap-2 mt-5">
                  {testimonials.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentTestimonial(idx)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        idx === currentTestimonial ? 'w-10 bg-red-400' : 'w-2 bg-white/20 hover:bg-white/40'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Right Side - Login Card */}
            <div className="order-1 lg:order-2">
              <div 
                className="relative max-w-md mx-auto"
                onMouseEnter={() => setHoverCard(true)}
                onMouseLeave={() => setHoverCard(false)}
              >
                {/* Glow effect */}
                <div className={`absolute -inset-6 bg-gradient-to-r from-red-500/30 via-red-500/30 to-orange-500/30 rounded-[3rem] blur-3xl transition-all duration-500 ${hoverCard ? 'opacity-100 scale-105' : 'opacity-60'}`}></div>
                
                {/* Main Card */}
                <div className="relative bg-slate-900/80 backdrop-blur-3xl border border-white/10 rounded-[2rem] p-8 shadow-2xl">
                  {renderAuthForm()}
                  
                  {/* Trust Indicators */}
                  <div className="mt-8 pt-6 border-t border-white/10">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-white/50">
                        <Shield className="w-4 h-4 text-emerald-400" />
                        <span className="text-xs font-medium">SSL Şifreli</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/50">
                        <Lock className="w-4 h-4 text-red-400" />
                        <span className="text-xs font-medium">GDPR Uyumlu</span>
                      </div>
                    </div>
                    
                    <p className="text-center text-white/30 text-xs leading-relaxed">
                      Giriş yaparak{' '}
                      <span className="text-white/50 hover:text-white cursor-pointer">Kullanım Şartları</span>
                      {' '}ve{' '}
                      <span className="text-white/50 hover:text-white cursor-pointer">Gizlilik Politikası</span>
                      &apos;nı kabul etmiş olursunuz.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="text-center py-6 border-t border-white/5">
          <div className="flex items-center justify-center gap-2 text-white/30 text-sm">
            <span>Made with</span>
            <Heart className="w-4 h-4 text-red-400 fill-red-400" />
            <span>in Sweden</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
